/* eslint-disable max-lines */
import { flexRender, useReactTable } from '@tanstack/react-table';
import {
  type ColumnDef,
  type ColumnFiltersState,
  getCoreRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type PaginationState,
  type RowData,
  type SortingState,
} from '@tanstack/table-core';
import { XIcon } from 'lucide-react';
import { Fragment, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type { TranslationKeys } from '@/lib/translations/types';

type Props<T extends RowData> = {
  data: T[];
  columns: ColumnDef<T>[];
  headersTranslationPath: string;
  optionsTranslationPaths?: Record<string, string | undefined>;
  initialSorting?: SortingState;
  initialPagination?: Partial<PaginationState>;
  noResultsMessage?: TranslationKeys;
};

export const DataTable = <T extends RowData>({
  data,
  columns,
  headersTranslationPath,
  optionsTranslationPaths = {},
  initialPagination = {},
  initialSorting = [],
  noResultsMessage = 'global.noResults',
}: Props<T>): JSX.Element => {
  const [sorting, setSorting] = useState<SortingState>(initialSorting);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
    ...initialPagination,
  });

  const table = useReactTable({
    data,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onPaginationChange: setPagination,
    getFilteredRowModel: getFilteredRowModel(),
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getSortedRowModel: getSortedRowModel(),
    state: {
      sorting,
      columnFilters,
      pagination,
    },
    defaultColumn: {
      size: 100 / columns.length,
    },
  });

  return (
    <div className="w-full">
      <div className="flex space-x-2 p-1">
        {table.getState().columnFilters.map(({ value, id }) => (
          <div
            key={id}
            className="pl-5 pr-2 py-1 rounded-full bg-background flex items-center gap-2 border"
          >
            <div>
              <h6 className="text-xs block text-gray-500">
                <FormattedMessage
                  id={
                    `${headersTranslationPath}.${id}.header` as TranslationKeys
                  }
                />
              </h6>
              <p className="text-xs block">
                {optionsTranslationPaths[id] !== undefined ? (
                  <FormattedMessage
                    id={
                      `${optionsTranslationPaths[id]}.${value as string}` as TranslationKeys
                    }
                  />
                ) : (
                  (value as string)
                )}
              </p>
            </div>
            <Button
              variant="ghost"
              onClick={() =>
                table.setColumnFilters(
                  table.getState().columnFilters.filter(f => f.id !== id),
                )
              }
              className="p-1 rounded-full h-6 w-6"
            >
              <XIcon />
            </Button>
          </div>
        ))}
      </div>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map(headerGroup => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map(header => (
                  <TableHead key={header.id} className="text-xs font-normal">
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.length > 0 ? (
              table.getRowModel().rows.map(row => (
                <Fragment key={row.id}>
                  <TableRow
                    data-state={row.getIsSelected() && 'selected'}
                    className="text-xs"
                  >
                    {row.getVisibleCells().map(cell => (
                      <TableCell
                        key={cell.id}
                        style={{
                          width: cell.column.columnDef.size + '%',
                        }}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                </Fragment>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center text-xs"
                >
                  <FormattedMessage id={noResultsMessage} />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <div className="flex items-center justify-end space-x-2 mt-4">
        <div>
          <p className="text-xs">
            {table.getRowModel().rows.length > 0 && (
              <FormattedMessage
                id="global.paginationOf"
                values={{
                  start:
                    table.getState().pagination.pageIndex *
                      table.getState().pagination.pageSize +
                    1,
                  end:
                    table.getState().pagination.pageIndex *
                      table.getState().pagination.pageSize +
                    table.getRowModel().rows.length,
                  total: table.getRowCount(),
                }}
              />
            )}
          </p>
        </div>
        <div className="space-x-2">
          <Button
            variant="outline"
            size="sm"
            className="text-xs"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            <FormattedMessage id="global.previous" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="text-xs"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            <FormattedMessage id="global.next" />
          </Button>
        </div>
      </div>
    </div>
  );
};
